import React, { useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import * as DocumentPicker from "expo-document-picker";
import { FontFamilies } from "@/constants/Typography";
import { useBillUpload } from "@/hooks/useBillUpload";
import { showError } from "@/utils/toast";
import { AnimatedStatusText } from "./AnimatedStatusText";
import { AnimatedProgressBar } from "./AnimatedProgressBar";

type Props = {
  visible: boolean;
  onClose: () => void;
};

export const UploadBillDialog: React.FC<Props> = ({ visible, onClose }) => {
  const [selectedFile, setSelectedFile] =
    useState<DocumentPicker.DocumentPickerAsset | null>(null);

  const { isUploading, uploadProgress, uploadStatus, uploadBill } =
    useBillUpload(
      () => {
        // On successful upload, close the dialog
        setSelectedFile(null);
        onClose();
      },
      () => {
        // On upload error, reset and close the dialog
        setSelectedFile(null);
        onClose();
      }
    );

  const handleSelectFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];

        // Check file size (10MB limit)
        if (file.size && file.size > 10 * 1024 * 1024) {
          showError("File size must be less than 10MB");
          return;
        }

        setSelectedFile(file);
        // Auto-upload after selection using the custom hook
        await uploadBill(file);
      }
    } catch (error) {
      console.error("Error picking document:", error);
      showError("Failed to pick document");
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      onClose();
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.dialog}>
          <View style={styles.header}>
            <Text style={styles.title}>Upload Bill</Text>
            <TouchableOpacity onPress={handleClose} disabled={isUploading}>
              <Ionicons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>

          {!selectedFile && !isUploading ? (
            <TouchableOpacity
              style={styles.uploadArea}
              onPress={handleSelectFile}
            >
              <Ionicons name="cloud-upload-outline" size={48} color="#9CA3AF" />
              <Text style={styles.uploadText}>Tap to select file</Text>
              <Text style={styles.uploadSubtext}>
                PDF or image • Maximum 10MB
              </Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.uploadingContainer}>
              <ActivityIndicator size="large" color="#0F9129" />
              <Text style={styles.uploadingText}>
                {selectedFile?.name || "Uploading..."}
              </Text>
              <AnimatedStatusText
                status={uploadStatus}
                style={styles.statusText}
              />
              <AnimatedProgressBar
                progress={uploadProgress}
                containerStyle={styles.progressBar}
                height={6}
                backgroundColor="#E5E7EB"
                progressColor="#0F9129"
              />
              <Text style={styles.progressText}>{uploadProgress}%</Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  dialog: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    width: "90%",
    maxWidth: 400,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#1F2937",
  },
  uploadArea: {
    borderWidth: 2,
    borderColor: "#E5E7EB",
    borderStyle: "dashed",
    borderRadius: 12,
    padding: 40,
    alignItems: "center",
  },
  uploadText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#374151",
    marginTop: 12,
  },
  uploadSubtext: {
    fontSize: 14,
    color: "#9CA3AF",
    marginTop: 4,
  },
  uploadingContainer: {
    padding: 40,
    alignItems: "center",
  },
  uploadingText: {
    fontSize: 14,
    color: "#374151",
    marginTop: 16,
    marginBottom: 8,
  },
  statusText: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 12,
    fontFamily: FontFamilies.Inter.Medium,
  },
  progressBar: {
    width: "100%",
    height: 6,
    backgroundColor: "#E5E7EB",
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#0F9129",
  },
  progressText: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 8,
  },
});
